import { Component, OnInit, ViewChild } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { GlobalProductService } from 'src/app/services/global-product.service';
import { GlobalProductFormDialogComponent } from './global-product-form-dialog/global-product-form-dialog.component';
import { LinkedProductsDialogComponent } from './linked-products-dialog/linked-products-dialog.component';
import { CsvImportDialogComponent } from './csv-import-dialog/csv-import-dialog.component';
import { ReferenceImportDialogComponent } from './reference-import-dialog/reference-import-dialog.component';
import { GlobalCategoryService } from 'src/app/services/global-category.service';
import { GlobalCategoriesTabComponent } from './global-categories-tab/global-categories-tab.component';

@Component({
  selector: 'app-global-catalog',
  templateUrl: './global-catalog.component.html',
  styleUrls: ['./global-catalog.component.scss']
})
export class GlobalCatalogComponent implements OnInit {

  @ViewChild(GlobalCategoriesTabComponent) categoriesTab?: GlobalCategoriesTabComponent;

  platformProducts: any[] = [];
  chainProducts: any[] = [];
  platformTotal = 0;
  platformPage = 1;
  platformLimit = 20;
  platformTotalPages = 1;
  chainTotal = 0;
  chainPage = 1;
  chainLimit = 20;
  chainTotalPages = 1;
  loading = false;
  chainLoading = false;
  Math = Math;

  platformSearch = '';
  platformAgeRestricted: boolean | null = null;
  platformHasDeposit: boolean | null = null;
  platformMainGroup = '';
  platformDiscountGroup = '';
  platformGlobalCategoryId = '';

  globalCategoriesForFilter: any[] = [];

  role: string = '';
  chainId: string = '';
  showReferenceMenu = false;

  constructor(
    private globalProductService: GlobalProductService,
    private globalCategoryService: GlobalCategoryService,
    private dialogService: NbDialogService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  get isAdmin(): boolean {
    return this.role === 'admin';
  }

  ngOnInit(): void {
    this.role = localStorage.getItem('role') || '';
    const chainIdRaw = localStorage.getItem('chainId');
    if (chainIdRaw) {
      try {
        const parsed = JSON.parse(chainIdRaw);
        this.chainId = Array.isArray(parsed) ? parsed[0] : parsed;
      } catch {
        this.chainId = chainIdRaw;
      }
    }
    this.loadGlobalCategoriesForFilter();
    this.loadPlatformProducts();
    if (this.showChainTab) {
      this.loadChainProducts();
    }
  }

  onCatalogTabChange(tab: { tabTitle?: string }): void {
    if (tab?.tabTitle === 'Categories') {
      this.categoriesTab?.loadCategories();
    }
  }

  loadGlobalCategoriesForFilter(): void {
    this.globalCategoryService.listCategories({ status: '1' }).subscribe({
      next: (res: any) => {
        this.globalCategoriesForFilter = this.globalCategoryService.extractListData(res).categories;
      },
      error: () => {
        this.globalCategoriesForFilter = [];
      }
    });
  }

  categoryFilterLabel(cat: any): string {
    const depth = cat.depth ?? 0;
    return '\u00a0\u00a0'.repeat(depth) + (cat.path || cat.name);
  }

  get showChainTab(): boolean {
    return this.role === 'admin' || this.role === 'chain_manager';
  }

  loadPlatformProducts(): void {
    this.loading = true;
    const filters: any = { page: this.platformPage, limit: this.platformLimit };
    if (this.platformSearch) filters['search'] = this.platformSearch;
    if (this.platformAgeRestricted !== null) filters['ageRestricted'] = this.platformAgeRestricted;
    if (this.platformHasDeposit !== null) filters['hasDeposit'] = this.platformHasDeposit;
    if (this.platformMainGroup) filters['mainGroup'] = this.platformMainGroup;
    if (this.platformDiscountGroup) filters['discountGroup'] = this.platformDiscountGroup;
    if (this.platformGlobalCategoryId) filters['globalCategoryId'] = this.platformGlobalCategoryId;

    this.globalProductService.listPlatformProducts(filters).subscribe({
      next: (res: any) => {
        this.platformProducts = res?.response?.data?.products || res?.data?.products || res?.data || [];
        this.platformTotal = res?.response?.data?.total || 0;
        this.platformTotalPages = Math.ceil(this.platformTotal / this.platformLimit) || 1;
        this.loading = false;
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.response?.message || 'Failed to load global products');
        this.loading = false;
      }
    });
  }

  onPlatformPageChange(page: number): void {
    if (page < 1 || page > this.platformTotalPages) return;
    this.platformPage = page;
    this.loadPlatformProducts();
  }

  getPlatformPaginationPages(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    if (this.platformTotalPages <= maxVisible) {
      for (let i = 1; i <= this.platformTotalPages; i++) pages.push(i);
    } else {
      let start = Math.max(2, this.platformPage - 2);
      let end = Math.min(this.platformTotalPages - 1, this.platformPage + 2);
      if (this.platformPage <= 3) { start = 2; end = Math.min(this.platformTotalPages - 1, 5); }
      else if (this.platformPage >= this.platformTotalPages - 2) { start = Math.max(2, this.platformTotalPages - 4); end = this.platformTotalPages - 1; }
      for (let i = start; i <= end; i++) pages.push(i);
    }
    return pages;
  }

  loadChainProducts(): void {
    if (!this.chainId) return;
    this.chainLoading = true;
    const filters: any = { page: this.chainPage, limit: this.chainLimit };
    this.globalProductService.listChainProducts(this.chainId, filters).subscribe({
      next: (res: any) => {
        this.chainProducts = res?.response?.data?.products || res?.data?.products || res?.data || [];
        this.chainTotal = res?.response?.data?.total || 0;
        this.chainTotalPages = Math.ceil(this.chainTotal / this.chainLimit) || 1;
        this.chainLoading = false;
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.response?.message || 'Failed to load chain products');
        this.chainLoading = false;
      }
    });
  }

  onChainPageChange(page: number): void {
    if (page < 1 || page > this.chainTotalPages) return;
    this.chainPage = page;
    this.loadChainProducts();
  }

  getChainPaginationPages(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    if (this.chainTotalPages <= maxVisible) {
      for (let i = 1; i <= this.chainTotalPages; i++) pages.push(i);
    } else {
      let start = Math.max(2, this.chainPage - 2);
      let end = Math.min(this.chainTotalPages - 1, this.chainPage + 2);
      if (this.chainPage <= 3) { start = 2; end = Math.min(this.chainTotalPages - 1, 5); }
      else if (this.chainPage >= this.chainTotalPages - 2) { start = Math.max(2, this.chainTotalPages - 4); end = this.chainTotalPages - 1; }
      for (let i = start; i <= end; i++) pages.push(i);
    }
    return pages;
  }

  onPlatformSearch(): void {
    this.platformPage = 1;
    this.loadPlatformProducts();
  }

  clearPlatformFilters(): void {
    this.platformSearch = '';
    this.platformAgeRestricted = null;
    this.platformHasDeposit = null;
    this.platformMainGroup = '';
    this.platformDiscountGroup = '';
    this.platformGlobalCategoryId = '';
    this.platformPage = 1;
    this.loadPlatformProducts();
  }

  openAddDialog(): void {
    this.dialogService.open(GlobalProductFormDialogComponent, {
      context: { product: null, chainId: null },
      hasBackdrop: true,
      closeOnBackdropClick: false
    }).onClose.subscribe((saved: boolean) => {
      if (saved) this.loadPlatformProducts();
    });
  }

  openEditDialog(product: any): void {
    this.dialogService.open(GlobalProductFormDialogComponent, {
      context: { product: product, chainId: null },
      hasBackdrop: true,
      closeOnBackdropClick: false
    }).onClose.subscribe((saved: boolean) => {
      if (saved) this.loadPlatformProducts();
    });
  }

  openLinkedProductsDialog(product: any): void {
    this.dialogService.open(LinkedProductsDialogComponent, {
      context: { globalProduct: product },
      hasBackdrop: true,
      closeOnBackdropClick: true
    });
  }

  syncProduct(product: any): void {
    this.spinner.show();
    this.globalProductService.forceSync(product._id).subscribe({
      next: () => {
        this.spinner.hide();
        this.toastr.success('Sync triggered successfully');
      },
      error: (err: any) => {
        this.spinner.hide();
        this.toastr.error(err?.error?.response?.message || 'Sync failed');
      }
    });
  }

  deleteProduct(product: any): void {
    Swal.fire({
      title: 'Delete Global Product?',
      text: `This will delete "${product.name}" from the platform catalog.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545'
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.globalProductService.deleteGlobalProduct(product._id).subscribe({
          next: () => {
            this.spinner.hide();
            this.toastr.success('Product deleted');
            this.loadPlatformProducts();
          },
          error: (err: any) => {
            this.spinner.hide();
            this.toastr.error(err?.error?.response?.message || 'Delete failed');
          }
        });
      }
    });
  }

  openImportDialog(): void {
    this.showReferenceMenu = false;
    this.dialogService.open(CsvImportDialogComponent, {
      context: { chainId: null },
      hasBackdrop: true,
      closeOnBackdropClick: false
    }).onClose.subscribe((saved: boolean) => {
      if (saved) this.loadPlatformProducts();
    });
  }

  openReferenceImportDialog(kind: 'dsk' | 'vendor'): void {
    this.showReferenceMenu = false;
    this.dialogService.open(ReferenceImportDialogComponent, {
      context: { kind },
      hasBackdrop: true,
      closeOnBackdropClick: false
    }).onClose.subscribe(() => { /* reference data only */ });
  }

  openChainImportDialog(): void {
    this.dialogService.open(CsvImportDialogComponent, {
      context: { chainId: this.chainId },
      hasBackdrop: true,
      closeOnBackdropClick: false
    }).onClose.subscribe((saved: boolean) => {
      if (saved) this.loadChainProducts();
    });
  }

  openAddChainDialog(): void {
    this.dialogService.open(GlobalProductFormDialogComponent, {
      context: { product: null, chainId: this.chainId },
      hasBackdrop: true,
      closeOnBackdropClick: false
    }).onClose.subscribe((saved: boolean) => {
      if (saved) this.loadChainProducts();
    });
  }

  openEditChainDialog(product: any): void {
    this.dialogService.open(GlobalProductFormDialogComponent, {
      context: { product: product, chainId: this.chainId },
      hasBackdrop: true,
      closeOnBackdropClick: false
    }).onClose.subscribe((saved: boolean) => {
      if (saved) this.loadChainProducts();
    });
  }

  deleteChainProduct(product: any): void {
    Swal.fire({
      title: 'Delete Chain Product?',
      text: `This will delete "${product.name}" from the chain catalog.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545'
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.globalProductService.deleteGlobalProduct(product._id).subscribe({
          next: () => {
            this.spinner.hide();
            this.toastr.success('Product deleted');
            this.loadChainProducts();
          },
          error: (err: any) => {
            this.spinner.hide();
            this.toastr.error(err?.error?.response?.message || 'Delete failed');
          }
        });
      }
    });
  }
}
